import * as React from "react";
import { createPortal } from "./browserFenster/fensterPortal";
import { Z_LAYERS } from "./zLayers";
import "../redesign/contactRedesign.css";
import { alleFenster, aktivesDokument } from "./browserFenster/fensterRegister";

/**
 * Schlanke .kvp-rd-Bestätigung für das Fenster-System. Ersetzt BEIDE alten
 * Bestätigungen: den Fluent-Guard (Routen-/Menüwechsel bei offenem Fenster)
 * UND das native window.confirm am X-Button. Kein Fluent.
 *
 * Drei Aktionen: Verwerfen (destruktiv), Minimieren (sekundär), Abbrechen
 * (dezent/Ghost; zusätzlich ESC + Backdrop-Klick). Was „Abbrechen" konkret
 * tut, bestimmt der Aufrufer über onCancel (X-Close: nur Dialog schließen;
 * Nav-Guard: zur Fenster-Route zurück). Portaliert nach document.body,
 * Wurzel trägt `kvp-rd` (Tokens); z-index ganz oben aus zLayers.
 */
export interface WindowConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: string;
  discardLabel?: string;
  minimizeLabel?: string;
  cancelLabel?: string;
  /** Verwerfen (destruktiv). */
  onDiscard: () => void;
  /** Minimieren (für später aufheben). */
  onMinimize: () => void;
  /** Abbrechen/Bleiben — auch ESC + Backdrop. */
  onCancel: () => void;
}

const WarnIcon = (
  <svg
    width={17}
    height={17}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M10 2.5 1.8 16.5h16.4L10 2.5Z" />
    <path d="M10 8v3.5M10 14.2v.2" />
  </svg>
);

const WindowConfirmDialog: React.FC<WindowConfirmDialogProps> = ({
  open,
  title = "Ungespeicherte Eingaben",
  message = "Dieses Fenster enthält ungespeicherte Eingaben. Verwerfen, oder für später minimieren?",
  discardLabel = "Verwerfen",
  minimizeLabel = "Minimieren",
  cancelLabel = "Abbrechen",
  onDiscard,
  onMinimize,
  onCancel,
}) => {
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  // A11y: Fokus beim Öffnen in den Dialog setzen (der ersetzte Fluent-Dialog/
  // window.confirm hatten Fokus-Management; der Custom-Dialog muss es selbst tun).
  // Erster Button = „Abbrechen" (ungefährlicher Default).
  React.useEffect(() => {
    if (!open) return;
    cardRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [open]);

  // Einfacher Focus-Trap: Tab/Shift+Tab am ersten/letzten Button umbrechen.
  const onCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const buttons = cardRef.current?.querySelectorAll<HTMLButtonElement>("button");
    if (!buttons || buttons.length === 0) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (e.shiftKey && aktivesDokument().activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && aktivesDokument().activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="kvp-rd wcd-backdrop"
      style={{ zIndex: Z_LAYERS.windowConfirm }}
      role="presentation"
      onPointerDown={onCancel}
    >
      <div
        ref={cardRef}
        className="wcd-card"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={onCardKeyDown}
      >
        <div className="wcd-head">
          <span className="wcd-head-ico">{WarnIcon}</span>
          <span className="wcd-title">{title}</span>
        </div>
        <div className="wcd-body">{message}</div>
        <div className="wcd-foot">
          <button type="button" className="wcd-btn ghost" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className="wcd-btn" onClick={onMinimize}>
            {minimizeLabel}
          </button>
          <button type="button" className="wcd-btn danger" onClick={onDiscard}>
            {discardLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WindowConfirmDialog;
